/**
 * permissions.ts - mirrors the backend's role/permission names exactly
 * (see the backend's V2__seed_data.sql). Keeping these as typed constants
 * means a typo in a role check fails at COMPILE time, not silently at
 * runtime - and there's exactly one place to update if the backend's role
 * names ever change.
 *
 * Reminder (Section 6.2): these checks only improve the interface (hiding
 * buttons/menus the user can't use) - the backend remains the sole source
 * of truth for what's actually allowed. Never rely on a frontend check
 * alone to protect anything sensitive.
 */
import type { To } from 'react-router-dom';
import { ROUTES } from './routes';

export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  OPERATIONS_MANAGER: 'OPERATIONS_MANAGER',
  DISPATCHER: 'DISPATCHER',
  FINANCE_OFFICER: 'FINANCE_OFFICER',
  VERIFICATION_OFFICER: 'VERIFICATION_OFFICER',
  SUPPORT_AGENT: 'SUPPORT_AGENT',
  PARTNER: 'PARTNER',
  DRIVER: 'DRIVER',
  CLIENT: 'CLIENT',
  CORPORATE_ADMIN: 'CORPORATE_ADMIN',
  AGENT: 'AGENT',
} as const;

export type RoleName = (typeof ROLES)[keyof typeof ROLES];

/** Roles considered "TripLink staff" for the purposes of the /admin/* route guard. */
export const STAFF_ROLES: RoleName[] = [
  ROLES.SUPER_ADMIN,
  ROLES.OPERATIONS_MANAGER,
  ROLES.DISPATCHER,
  ROLES.FINANCE_OFFICER,
  ROLES.VERIFICATION_OFFICER,
  ROLES.SUPPORT_AGENT,
];

/**
 * getPostLoginRoute - every account type shares the same /login form, so
 * this is the one place that decides which portal a person actually lands
 * on afterwards, based on their real roles (not `accountType`, for the same
 * reason RequireRole doesn't use it - see jwt.ts). Order matters: a staff
 * account takes priority over any other role it might also hold, and so on
 * down to CLIENT, the default for anyone with no other role.
 *
 * Imports ROUTES directly (not the reverse) since routes.ts has no
 * knowledge of roles - this file is the one that depends on both.
 */
export function getPostLoginRoute(roles: string[]): string {
  if (roles.some((role) => STAFF_ROLES.includes(role as RoleName))) {
    return ROUTES.adminDashboard;
  }
  if (roles.includes(ROLES.CORPORATE_ADMIN)) {
    return ROUTES.corporateDashboard;
  }
  if (roles.includes(ROLES.PARTNER)) {
    return ROUTES.partnerDashboard;
  }
  if (roles.includes(ROLES.DRIVER)) {
    return ROUTES.driverDashboard;
  }
  if (roles.includes(ROLES.AGENT)) {
    return ROUTES.agentDashboard;
  }
  return ROUTES.clientDashboard;
}

/**
 * PORTALS_REQUIRING_A_ROLE - the single-role-gated portals, keyed by the URL prefix a `from`
 * destination could fall under. Each one's *dashboard root* doubles as that portal's application
 * form for someone who doesn't hold the role yet (see the "No role check on the dashboard route
 * itself" comments in router.tsx for /partner and /agent) - only the pages *below* the root
 * actually require the role, so `dashboardRoot` is exempted from the check.
 *
 * /admin isn't listed here (it needs an ANY-OF-STAFF_ROLES match, not one fixed role, and unlike
 * these three, even its dashboard root itself requires the role - see
 * isPostLoginDestinationReachable below). /driver has no dashboard-root exception either (every
 * driver route, dashboard included, requires DRIVER), but fits this single-role shape. /corporate
 * is left out entirely - CorporateController authorizes per-request by "owns this
 * CorporateAccount", not by role, so every /corporate/* page is reachable by any authenticated
 * user regardless of role (see router.tsx's corporate block).
 */
const PORTALS_REQUIRING_A_ROLE: { prefix: string; role: RoleName; dashboardRoot?: string }[] = [
  { prefix: '/driver', role: ROLES.DRIVER },
  { prefix: '/partner', role: ROLES.PARTNER, dashboardRoot: ROUTES.partnerDashboard },
  { prefix: '/agent', role: ROLES.AGENT, dashboardRoot: ROUTES.agentDashboard },
];

/**
 * isPostLoginDestinationReachable - a coarse, path-prefix check of whether `pathname` is even in
 * the right ballpark for someone with `roles`, used ONLY to decide whether a post-login redirect
 * should honor an arbitrary `from` destination at all (see resolvePostLoginRoute below).
 *
 * Deliberately coarser than the real route guards (RequireRole/RequirePermission remain the actual
 * authority - a staff member missing one specific admin permission still correctly lands on
 * /unauthorized, exactly as it should): this only catches the common, jarring case of an account
 * with NONE of a whole portal's required role being sent straight back to a page it can never
 * reach at all, immediately after a successful login (e.g. bookmarking an /admin/* URL and logging
 * in with a client/driver/agent account - previously bounced to "Access denied" every time).
 */
export function isPostLoginDestinationReachable(pathname: string, roles: string[]): boolean {
  if (pathname.startsWith(ROUTES.adminDashboard)) {
    return roles.some((role) => STAFF_ROLES.includes(role as RoleName));
  }
  const portal = PORTALS_REQUIRING_A_ROLE.find((p) => pathname.startsWith(p.prefix));
  if (!portal) {
    return true; // everything else (public pages, /account/*, /booking/*, /corporate/*, ...) is open to any authenticated user
  }
  if (portal.dashboardRoot && pathname === portal.dashboardRoot) {
    return true;
  }
  return roles.includes(portal.role);
}

/**
 * resolvePostLoginRoute - what LoginPage/RequireGuest actually navigate to once someone is
 * authenticated. Honors `from` (the page they were trying to reach) only when it's plausibly
 * reachable for their actual roles; otherwise falls back to their own portal's default, rather
 * than sending them straight into a guard that's certain to redirect to /unauthorized.
 */
export function resolvePostLoginRoute(from: To | undefined, roles: string[]): To | string {
  if (from) {
    const pathname = typeof from === 'string' ? from : (from.pathname ?? '');
    if (isPostLoginDestinationReachable(pathname, roles)) {
      return from;
    }
  }
  return getPostLoginRoute(roles);
}

/**
 * OTHER_PORTALS_BY_ROLE - the non-CLIENT portals a multi-role user (e.g.
 * CLIENT+AGENT, CLIENT+PARTNER) can also reach, keyed by the role that
 * unlocks each one. getPostLoginRoute only ever lands someone on their
 * single highest-priority portal, and none of the portal layouts otherwise
 * link to each other, so this is what PortalLayout/ClientLayout use to
 * surface "switch to your other account" links in the header menu.
 */
export const OTHER_PORTALS_BY_ROLE: { role: RoleName; label: string; route: string }[] = [
  { role: ROLES.PARTNER, label: 'Partner dashboard', route: ROUTES.partnerDashboard },
  { role: ROLES.AGENT, label: 'Agent dashboard', route: ROUTES.agentDashboard },
  { role: ROLES.CORPORATE_ADMIN, label: 'Corporate dashboard', route: ROUTES.corporateDashboard },
  { role: ROLES.DRIVER, label: 'Driver dashboard', route: ROUTES.driverDashboard },
];
